/**
 * Helper para enviar eventos desde el frontend a Cloud Logging.
 *
 * Cada evento se manda al callable `clientLog` que emite un structured log
 * en GCP Cloud Logging. Útil para debugging en producción donde el browser
 * console del cliente no está accesible para nosotros.
 *
 * Filtrado en Logs Explorer:
 *   resource.type="cloud_function"
 *   resource.labels.function_name="clientLog"
 *   jsonPayload.event="auth.logout.triggered"
 *
 * No bloquea el flujo: si el callable falla (offline, rate limit), el error
 * se silencia con un console.warn local. NUNCA debe romper la app por un log
 * que no llegó.
 *
 * Uso típico:
 *   import { logClient } from 'application/lib/client_log';
 *   logClient({
 *     event: 'auth.logout.triggered',
 *     level: 'error',
 *     message: 'Logout automático disparado',
 *     data: { source: 'useEffect', cause: 'roles missing' }
 *   });
 *
 * Eventos canónicos (mantener consistente para poder filtrar):
 *   auth.logout.triggered    - dispatch(logout()) se ejecutó
 *   auth.signin.error        - signInWithEmailAndPassword falló
 *   auth.roles.refetch       - roles() se redispatchea durante sesión activa
 *   client.error             - error JS capturado por error boundary global
 *   firestore.permission.denied - lectura/escritura denied por rules
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';

let _functionsInstance = null;

const getFunctions = () => {
  if (_functionsInstance) return _functionsInstance;
  try {
    _functionsInstance = firebase.app().functions();
    return _functionsInstance;
  } catch {
    return null;
  }
};

/**
 * Envía un evento al backend. Fire-and-forget: no se debe esperar la
 * promesa para no introducir latencia en el flujo del usuario.
 *
 * @param {object} opts
 * @param {string} opts.event   Nombre del evento (kebab/dot.case). Ej: 'auth.logout.triggered'
 * @param {string} [opts.level] 'debug' | 'info' | 'warn' | 'error'. Default 'info'.
 * @param {string} [opts.message] Mensaje legible del evento.
 * @param {object} [opts.data]  Payload arbitrario JSON-serializable (<256KB).
 */
export const logClient = ({ event, level = 'info', message, data } = {}) => {
  // Spiegel-local console para que se vea también en el DevTools del usuario
  // que reportó. El emoji facilita búsqueda visual en console del browser.
  const tag = { error: '🔴', warn: '🟡', info: '🔵', debug: '⚪' }[level] || '·';
  const localMsg = `${tag} [client-log] ${event}${message ? ': ' + message : ''}`;
  if (level === 'error') console.error(localMsg, data || '');
  else if (level === 'warn') console.warn(localMsg, data || '');
  else console.log(localMsg, data || '');

  // Stack trace para errors — útil cuando el evento es un trigger inesperado
  const stack = level === 'error' ? new Error(event).stack : null;

  const fns = getFunctions();
  if (!fns) return;

  try {
    const callable = fns.httpsCallable('clientLog');
    // Fire-and-forget. Limit timeout corto para no colgar nada.
    callable({
      event,
      level,
      message: message || null,
      data: data || null,
      stack,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      url: typeof window !== 'undefined' ? window.location.href : null,
    }).catch((err) => {
      // No relanzamos — un log fallido no debe romper la app.
      console.warn('[client-log] envío falló:', err?.message);
    });
  } catch (e) {
    console.warn('[client-log] no se pudo iniciar:', e?.message);
  }
};

export default logClient;
